import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { orderLines, bookingTotal } from "@/components/BookingDetail";

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// Times before noon are after-midnight arrivals, so they sort last.
const sortKey = (t) => {
  if (!t) return 9999;
  const [h, m] = t.split(":").map(Number);
  return (h < 12 ? h + 24 : h) * 60 + (m || 0);
};

const prettyDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function NightSheet() {
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || todayISO();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = `Soho69 · Night Sheet ${date}`;
    setRows(null);
    setError("");
    api.get("/bookings", { params: { date } })
      .then((r) => setRows(
        (r.data || [])
          .filter((b) => (b.type === "table" || b.type === "barorder") && b.status !== "cancelled")
          .sort((a, b) => sortKey(a.arrival_time) - sortKey(b.arrival_time))
      ))
      .catch((e) => setError(formatApiError(e)));
  }, [date]);

  const total = (rows || []).reduce((s, b) => s + bookingTotal(b), 0);

  return (
    <div data-testid="night-sheet-page" className="night-sheet min-h-screen bg-white text-black px-6 py-8 print:p-0" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
      <style>{`@page { size: A4 portrait; margin: 12mm; }
        @media print { html, body { background:#fff !important; } .no-print { display:none !important; } }
        body { background:#fff !important; }`}</style>

      <div className="no-print flex flex-wrap items-center gap-3 mb-6">
        <Link to="/admin" data-testid="night-sheet-back" className="px-4 py-2 border border-black rounded text-sm">← Command Centre</Link>
        <label className="text-sm flex items-center gap-2">
          Night
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setParams({ date: e.target.value })}
            data-testid="night-sheet-date-input"
            className="border border-black rounded px-2 py-1.5 text-sm bg-white text-black"
          />
        </label>
        <button type="button" onClick={() => window.print()} data-testid="night-sheet-print-btn" className="px-4 py-2 bg-black text-white rounded text-sm font-bold">
          Print
        </button>
      </div>

      <header className="border-b-2 border-black pb-3 mb-4">
        <div className="text-xs uppercase tracking-widest">Soho69 · Bar Night Sheet</div>
        <h1 className="text-2xl font-bold mt-1" data-testid="night-sheet-date">{prettyDate(date)}</h1>
        <div className="mt-1 text-sm flex gap-6">
          <span data-testid="night-sheet-count"><b>{rows ? rows.length : "…"}</b> bookings</span>
          <span data-testid="night-sheet-total">Night total: <b>€{total}</b></span>
        </div>
      </header>

      {error && <p className="text-sm" data-testid="night-sheet-error">{error}</p>}
      {rows && rows.length === 0 && <p className="text-sm" data-testid="night-sheet-empty">No table bookings or VIP pre-orders for this night.</p>}

      {rows && rows.length > 0 && (
        <table className="w-full text-sm border-collapse" data-testid="night-sheet-table">
          <thead>
            <tr className="border-b border-black text-left">
              <th className="py-1.5 pr-3 w-14">Time</th>
              <th className="py-1.5 pr-3">Table / Booking</th>
              <th className="py-1.5 pr-3">Customer</th>
              <th className="py-1.5 pr-3">Order</th>
              <th className="py-1.5 text-right w-16">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const lines = orderLines(b);
              return (
                <tr key={b.id} data-testid={`night-sheet-row-${b.id}`} className="border-b border-gray-400 align-top" style={{ breakInside: "avoid" }}>
                  <td className="py-2 pr-3 font-bold">{b.arrival_time || "—"}</td>
                  <td className="py-2 pr-3">{b.type === "barorder" ? "VIP Pre-Order" : b.table_name || "Table"}{b.party_size ? ` · ${b.party_size}p` : ""}</td>
                  <td className="py-2 pr-3">{b.name}<br />{b.whatsapp || "—"}</td>
                  <td className="py-2 pr-3">
                    {lines.length ? lines.map((l) => (
                      <div key={l.key} className="flex items-center gap-3 py-1">
                        <span data-testid={`night-sheet-tick-${b.id}-${l.key}`} className="inline-block shrink-0 border-2 border-black bg-white" style={{ width: "5mm", height: "5mm" }} />
                        <span>{l.qty}x {l.name}{l.price != null ? ` €${l.price}` : ""}</span>
                      </div>
                    )) : <i>No order saved</i>}
                  </td>
                  <td className="py-2 text-right font-bold">€{bookingTotal(b)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
